import React, { useEffect, useState } from 'react';
import { askOrbitAction } from '../../components/AskOrbit';
import { useSearchParams } from 'react-router-dom';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { AaiOpportunity, AaiRequest, AaiSkill, OpportunityReviewState, OwnerOverview } from '../../types/aai';
import {
  Chip, Divided, EmptyState, ErrorNote, ListRow, ListToolbar, Loading, PageBody, PageHead, Panel, RouteMark, Tabs,
} from '../../components/aa';
import { CustomerProjectsView } from '../client/ClientAgentsPage';
import { CapabilityDrawer } from './CapabilityDrawer';
import { OpportunityDrawer, askedFor, nextStepOf, stateLabelOf, stateToneOf } from './OpportunityDrawer';
import { ProjectRecordDrawer } from './ProjectRecordDrawer';
import { useRecordLinks } from '../../hooks/useRecordLinks';
import { CapabilityRow, ClarityRow, NEXT_COLUMN, NextCell, RowFoot, isReadyToUse, needsOwner, rowTitle } from './WorkRows';
import { relativeTime } from '../partner/format';
import { AccessRequestsQueue, MarketSearch } from '../aai/SkillsMarketplacePage';

/*
 * Projects, for the Owner: every piece of company work Orbit has routed.
 *
 *   Clarity        the requests: Projects, and requests on their way to one
 *   Opportunities  what the Solution Partner sent for the Owner's decision
 *   Quick Wins     published company capabilities, in business language
 *   Builds         the delivery surface that already existed, unchanged
 *
 * Use Cases folded in here: approved requests are the registry, so a second
 * list of the same rows under another name would disagree with this one.
 * There is no separate Skills tab: a Skill is how a Quick Win is implemented,
 * and the Owner reads the outcome, not the implementation.
 */

const TABS = [
  { id: 'all', label: 'Clarity' },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'quick_wins', label: 'Quick Wins' },
  { id: 'builds', label: 'Custom builds' },
];

type OppFilter = 'all' | 'awaiting_client' | 'confirmed' | 'routed' | 'declined';
const OPP_FILTERS: Array<{ id: OppFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'awaiting_client', label: 'Awaiting your decision' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'routed', label: 'Routed' },
  { id: 'declined', label: 'Declined' },
];

type WorkFilter = 'active' | 'needs_you' | 'in_progress' | 'waiting' | 'ready' | 'all';
const FILTERS: Array<{ id: WorkFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'needs_you', label: 'Needs you' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'ready', label: 'Ready to use' },
  { id: 'all', label: 'All' },
];

const MATCH: Record<WorkFilter, (r: AaiRequest) => boolean> = {
  active: (r) => r.status !== 'declined',
  needs_you: needsOwner,
  in_progress: (r) => !isReadyToUse(r) && (r.status === 'approved' || r.status === 'dept_approved'),
  waiting: (r) => r.status === 'pending' || r.status === 'needs_info',
  ready: isReadyToUse,
  all: () => true,
};

type WinKind = 'skill' | 'plugin';
const WIN_KINDS: Array<{ id: WinKind; label: string }> = [
  { id: 'skill', label: 'Skills' },
  { id: 'plugin', label: 'Plugins' },
];
const WIN_NOTE: Record<WinKind, string> = {
  skill: 'Proven ways of working your people can run. Ask for access if one is restricted.',
  plugin: 'Skills that bring their own connection, like an MCP server or an app.',
};
const WIN_GROUPS = [
  { key: 'provider', title: 'From Agents Anywhere', note: 'Built by us and kept working for every company.' },
  { key: 'company', title: 'From your company', note: 'Published by your own people, for your own people.' },
];
const winKindOf = (s: AaiSkill): WinKind => (s.shape === 'plugin' ? 'plugin' : 'skill');
const winGroupOf = (s: AaiSkill): string => (s.source === 'provider' ? 'provider' : 'company');

/*
 * Under an Opportunity's title: where it came from and when. Orbit reading the
 * dossier and a person asking for something are both Opportunities, so the row
 * names the origin rather than pretending they are the same.
 */
const oppFoot = (o: AaiOpportunity) => {
  let origin = 'Orbit found it';
  if (askedFor(o)) origin = o.requester_email || 'Somebody asked';
  if (!o.created_at) return origin;
  return `${origin} · ${relativeTime(o.created_at)}`;
};

const oppText = (o: AaiOpportunity) => o.partner_summary || o.statement;

/* `clientId` points the page at one of a provider's clients: the builder list
   then carries the expert assignment and planner door the provider uses. */
export const OwnerProjectsPage: React.FC<{ embedded?: boolean; clientId?: string }> = ({ embedded, clientId }) => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'all';
  const openParam = params.get('open');

  const requests = useAsync<{ scope: string; requests: AaiRequest[]; client_work?: AaiRequest[] }>(() => aaiApi.listRequests(), 'Could not load company work.');
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(() => aaiApi.listSkills(), 'Could not load company Quick Wins.');
  const overview = useAsync<OwnerOverview>(() => aaiApi.getOwnerOverview(), 'Could not load Opportunities.');

  const [filter, setFilter] = useState<WorkFilter>('active');
  const [oppFilter, setOppFilter] = useState<OppFilter>('all');
  const [winKind, setWinKind] = useState<WinKind>('skill');
  const [winQuery, setWinQuery] = useState('');
  const [reqQuery, setReqQuery] = useState('');
  const [oppQuery, setOppQuery] = useState('');
  const [openProject, setOpenProject] = useState<string | null>(null);
  const [openOpportunity, setOpenOpportunity] = useState<string | null>(null);
  const [openSkill, setOpenSkill] = useState<AaiSkill | null>(null);
  const links = useRecordLinks();

  // Each record opens as its own page. Embedded in a provider's client view
  // the records belong to that client, not to the reader's own company, so
  // they stay in a dialog there.
  const openProjectRecord = (id: string, replace = false) => {
    if (embedded) {
      setOpenProject(id);
      return;
    }
    links.project(id, replace);
  };
  const openOpportunityRecord = (id: string, replace = false) => {
    if (embedded) {
      setOpenOpportunity(id);
      return;
    }
    links.opportunity(id, replace);
  };
  const openQuickWinRecord = (skill: AaiSkill) => {
    if (embedded) {
      setOpenSkill(skill);
      return;
    }
    links.quickWin(skill.id);
  };

  useEffect(() => {
    if (!openParam) return;
    if (embedded) {
      if (tab === 'all') setOpenProject(openParam);
      if (tab === 'opportunities') setOpenOpportunity(openParam);
      return;
    }
    if (tab === 'all') links.project(openParam, true);
    if (tab === 'opportunities') links.opportunity(openParam, true);
  }, [openParam, tab, embedded, links]);

  // Only the tab changes: a page that embeds this one (Client projects) keeps
  // its own params, like the picked client, in the same address.
  const setTab = (id: string) => setParams((now) => {
    const next = new URLSearchParams(now);
    next.set('tab', id);
    next.delete('open');
    return next;
  }, { replace: true });

  const all = requests.data?.requests || [];
  const reqNeedle = reqQuery.trim().toLowerCase();
  const rows = all.filter(MATCH[filter]).filter((r) => !reqNeedle
    || `${r.title} ${r.detail || ''} ${r.requester_name || ''} ${r.requester_email} ${r.department || ''}`.toLowerCase().includes(reqNeedle));
  const quickWins = (skills.data?.skills || []).filter((s) => s.published);
  const winCounts = WIN_KINDS.map((k) => ({ ...k, count: quickWins.filter((s) => winKindOf(s) === k.id).length }));
  const winNeedle = winQuery.trim().toLowerCase();
  const winRows = quickWins.filter((s) => winKindOf(s) === winKind
    && (winNeedle.length < 2 || `${s.name} ${s.problem || ''}`.toLowerCase().includes(winNeedle)));
  const winGroups = WIN_GROUPS.map((g) => ({ ...g, rows: winRows.filter((s) => winGroupOf(s) === g.key) })).filter((g) => g.rows.length > 0);
  const opportunities = overview.data?.opportunities || [];
  const oppNeedle = oppQuery.trim().toLowerCase();
  const oppRows = opportunities.filter((o) => (oppFilter === 'all' || o.review_state === oppFilter)
    && (!oppNeedle || `${o.title || ''} ${oppText(o) || ''} ${o.requester_email || ''}`.toLowerCase().includes(oppNeedle)));
  const oppCounts = OPP_FILTERS.map((f) => ({
    ...f,
    count: f.id === 'all' ? opportunities.length : opportunities.filter((o) => o.review_state === (f.id as OpportunityReviewState)).length,
  }));
  const opportunity = opportunities.find((o) => o.id === openOpportunity) || null;
  const reloadAll = () => {
    requests.reload();
    overview.reload();
  };
  // A request is decided where requests are decided. Opening the partner's
  // decision drawer on one would offer actions its own gate refuses, so it
  // opens its record instead.
  const openOpportunityRow = (o: AaiOpportunity) => {
    if (askedFor(o)) {
      openProjectRecord(o.id);
      return;
    }
    openOpportunityRecord(o.id);
  };

  const body = (
    <>
      {!embedded && (
        <PageHead
          align="center"
          title="Projects"
          actions={askOrbitAction()}
        />
      )}

      <Tabs
        tabs={[
          { id: 'all', label: 'Clarity', count: all.filter((r) => r.status !== 'declined').length },
          { id: 'opportunities', label: 'Opportunities', count: opportunities.length },
          { id: 'quick_wins', label: 'Quick Wins', count: quickWins.length },
          TABS[3],
        ]}
        active={tab}
        onChange={setTab}
        label="Project type"
      />

      {tab === 'all' && (
        <Panel>
          <ListToolbar
            chips={FILTERS}
            active={filter}
            onChipChange={(id) => setFilter(id as WorkFilter)}
            chipsLabel="Work state"
            query={reqQuery}
            onQueryChange={setReqQuery}
            placeholder="Search clarity"
            note={`${rows.length} in this view`}
          />
          {requests.loading && <Loading label="Loading company work" />}
          {requests.error && <div className="px-6 py-5"><ErrorNote message={requests.error} /></div>}
          {!requests.loading && !requests.error && rows.length === 0 && (
            <EmptyState title="Nothing is waiting here" reason="Change the filter or the search. Work arrives when someone asks Orbit or you confirm an Opportunity." />
          )}
          <Divided>
            {rows.map((r) => <ClarityRow key={r.id} request={r} workspaceId={localStorage.getItem('client_id')} onOpen={openProjectRecord} />)}
          </Divided>
        </Panel>
      )}

      {tab === 'opportunities' && (
        <Panel>
          <ListToolbar
            chips={oppCounts}
            active={oppFilter}
            onChipChange={(id) => setOppFilter(id as OppFilter)}
            chipsLabel="Opportunity state"
            query={oppQuery}
            onQueryChange={setOppQuery}
            placeholder="Search opportunities"
            note="Anything worth looking at, whether Orbit found it or somebody asked for it. Not yet a Project."
          />
          {overview.loading && <Loading label="Loading Opportunities" />}
          {overview.error && <div className="px-6 py-5"><ErrorNote message={overview.error} /></div>}
          {!overview.loading && !overview.error && oppRows.length === 0 && (
            <EmptyState title="Nothing in this view" reason="Opportunities appear here when Orbit finds one in the company dossier, when somebody asks for something, or when the Solution Partner sends one for your decision." />
          )}
          <Divided>
            {oppRows.map((o) => (
              <ListRow
                key={o.id}
                mark={<RouteMark glyph="O" size="lg" />}
                dense
                title={o.title || rowTitle(o.title, oppText(o))}
                foot={<RowFoot chips={<Chip tone={stateToneOf(o)} dot>{stateLabelOf(o)}</Chip>} text={oppFoot(o)} />}
                meta={[{ label: 'Next step', value: <NextCell>{nextStepOf(o)}</NextCell> }]}
                metaWidth={NEXT_COLUMN}
                onClick={() => openOpportunityRow(o)}
                arrow
              />
            ))}
          </Divided>
        </Panel>
      )}

      {tab === 'quick_wins' && (
        <Panel>
          <ListToolbar
            chips={winCounts}
            active={winKind}
            onChipChange={(id) => setWinKind(id as WinKind)}
            chipsLabel="Quick Win kind"
            query={winQuery}
            onQueryChange={setWinQuery}
            placeholder="What are you trying to do?"
            note={WIN_NOTE[winKind]}
          />
          <div className="px-5 pt-4 md:px-6">
            <AccessRequestsQueue onDecided={skills.reload} />
            <MarketSearch value={winQuery} onChange={setWinQuery} matched={winRows.length} hideInput />
          </div>
          {skills.loading && <Loading label="Loading Quick Wins" />}
          {skills.error && <div className="px-6 py-5"><ErrorNote message={skills.error} /></div>}
          {!skills.loading && !skills.error && winRows.length === 0 && winNeedle.length < 2 && (
            <EmptyState title={`No ${winKind}s yet`} reason="A Quick Win appears here once a capability is published for the company or a team." />
          )}
          {winGroups.map((g) => (
            <React.Fragment key={g.key}>
              <div className="px-5 pt-3 pb-2 md:px-6">
                <div className="text-[13px] font-semibold text-ink">{g.title}</div>
                <div className="text-[12px] text-ink-dim">{g.note}</div>
              </div>
              <Divided>
                {g.rows.map((s) => <CapabilityRow key={s.id} skill={s} onOpen={openQuickWinRecord} />)}
              </Divided>
            </React.Fragment>
          ))}
        </Panel>
      )}

      {tab === 'builds' && !clientId && <CustomerProjectsView />}
      {tab === 'builds' && clientId && <CustomerProjectsView providerScope="owner" lock="client" onlyClientId={clientId} />}

      {openProject && <ProjectRecordDrawer requestId={openProject} onClose={() => setOpenProject(null)} onChanged={reloadAll} />}
      {opportunity && <OpportunityDrawer opportunity={opportunity} onClose={() => setOpenOpportunity(null)} onChanged={reloadAll} />}
      {openSkill && <CapabilityDrawer skill={openSkill} onClose={() => setOpenSkill(null)} />}
    </>
  );

  if (embedded) return body;
  return <PageBody>{body}</PageBody>;
};

export default OwnerProjectsPage;
